<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { ProjectDeleteDocument } from '@/gql/graphql';
import {
  BAlert,
  BBadge,
  BButton,
  BFormInput,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  projectName: string;
  projectId: number;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});
const projectNameConfirmation = ref<string>('');

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ProjectDeleteDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.projectDelete) {
    console.debug('Project deleted:', res.data.projectDelete);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Project deletion failed: ' + res.data;
    console.error('Project deletion failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error deleting project:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onDeletionConfirmed() {
  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({ id: props.projectId });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Storage Object Rename -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="trash" class="me-2" />
        Delete project
      </template>
      <!-- Content -->
      <div>
        <!-- Delete? Confirmation Message -->
        <h5>Delete {{ props.projectName }}?</h5>
        <b-badge variant="danger" class="mb-2">
          <font-awesome-icon icon="exclamation-triangle" />
          This action is irreversible
        </b-badge>
        <p>
          <strong> All associated data will be permanently deleted. </strong>
        </p>
        <p>
          If you wish to proceed with deletion, please type in the name of the
          project and click the button bellow.
        </p>
        <!-- Input for bucket name -->
        <p>
          <strong
            >Type
            <span class="confirmation-text">{{ props.projectName }} </span>
            for confirmation:</strong
          >
        </p>
        <b-form-input
          v-model="projectNameConfirmation"
          type="text"
          placeholder="Type the name of the project to confirm deletion"
          :disabled="mutationInProgress || mutationShowSuccess"
          class="mb-3"
        ></b-form-input>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="
            mutationInProgress ||
            mutationShowSuccess ||
            projectNameConfirmation !== props.projectName
          "
          @click="onDeletionConfirmed"
        >
          <span v-if="mutationInProgress">
            Deleting Project
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Project Deleted
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Delete Project </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<style scoped>
.confirmation-text {
  color: white;
  background-color: #6c757d;
  border-radius: 0.25rem;
  padding: 0.15rem 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
}
</style>
